import { useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldError, FieldErrors, FieldValues } from "react-hook-form";
import { InferType, ObjectSchema } from "yup";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { compact, difference, startCase } from "lodash";

import { ValidationTranslateKey } from "../i18n/i18n.types";

interface YupTranslatedMessage {
  key: ValidationTranslateKey;
  fieldLabel: string | undefined;
}

type ErrorsType<T> = Record<string, never> | FieldErrors<Partial<T>>;
type ErrorsTypeWithoutEmptyObject<T> = Exclude<
  ErrorsType<T>,
  Record<string, never>
>;

const ERROR_KEYS = ["ref", "type", "message"];
const CUSTOM_VALIDATION_TYPES = ["unique"];
type CustomValidationType = "unique";

const isCustomError = (type: string): type is CustomValidationType =>
  CUSTOM_VALIDATION_TYPES.includes(type);

const customError = (
  t: TFunction<"validation">,
  field: string,
  type: CustomValidationType
) => t(`yup.custom.${type}`, { field });

const localizeError = (
  t: TFunction<"validation">,
  fieldName: string,
  error: FieldError
) => {
  const { ref, type, message: errorMessage } = error;
  const base = { ref, type };

  const sourceMessage = errorMessage as unknown as
    | YupTranslatedMessage
    | undefined;

  // If the field has a custom label defined by calling `label` in the Yup schema, we want to use that. If not, we
  // fallback to the name of the field defined for the form.
  const field = sourceMessage?.fieldLabel ?? startCase(fieldName);

  if (isCustomError(type)) {
    // A custom validation provided by yup's `test`. For example, this could be a uniqueness requirement for an array.
    return { ...base, message: customError(t, field, type) };
  } else if (typeof sourceMessage === "string") {
    // A custom message provided in the yup schema. For example, overriding the message for a given validation.
    return { ...base, message: sourceMessage };
  } else {
    // All other typical validation errors.
    const message =
      errorMessage && sourceMessage ? t(sourceMessage.key, { field }) : "";
    return { ...base, message };
  }
};

const localizeErrors = <T extends FieldValues>(
  t: TFunction<"validation">,
  errorsToLocalize: ErrorsType<T>
) => {
  const errors = errorsToLocalize as ErrorsTypeWithoutEmptyObject<T>;

  return Object.keys(errors).reduce((previous, fieldName) => {
    let error = errors[fieldName as keyof T] as FieldError;

    if (error instanceof Array) {
      error = compact(error)[0] as FieldError;
    }
    const nestedFields = difference(Object.keys(error), ERROR_KEYS);

    const newErrors: Record<string, ReturnType<typeof localizeError>> = {
      ...previous,
    };

    nestedFields.forEach((field) => {
      const nestedResult = localizeError(
        t,
        `${fieldName}.${field}`,
        (error as unknown as Record<string, FieldError>)[field]
      );
      newErrors[`${fieldName}.${field}`] = nestedResult;
    });

    const result = localizeError(t, fieldName, error);
    newErrors[fieldName] = result;

    return newErrors;
  }, {});
};

const useLocalizedYupResolver = <T extends FieldValues>(
  schema: ObjectSchema<T>
) => {
  const { t } = useTranslation("validation");

  return useCallback(
    async (data: InferType<typeof schema>) => {
      const resolverForSchema = yupResolver(schema);

      // These two arguments are best guesses to fill out the function call:
      // https://github.com/react-hook-form/resolvers/blob/master/yup/src/yup.ts#L62
      const context = {};
      const options = { fields: {}, shouldUseNativeValidation: undefined };

      const { values, errors } = await resolverForSchema(
        data,
        context,
        options
      );

      // For reference, see the resolver in react-hook-form's code here: https://github.com/react-hook-form/react-hook-form/blob/7cdad77ae13190e6b491d9b4b30d89709229b52b/app/src/customSchemaValidation.tsx#L23
      return {
        values,
        errors: localizeErrors(t, errors),
      };
    },
    [schema, t]
  );
};

export default useLocalizedYupResolver;
